<script setup lang="ts">
import {capturePage, CAPTURE_SKIP} from '../util/page_capture';
import { ref, computed, watch, nextTick, onBeforeUnmount, createApp } from 'vue';
import GrowingCell from 'components/GrowingCell.vue';

import nurroAtHome from '../../static/nurro/nurro-at-home.png';
import nurroCapeCard from '../../static/nurro/nurro-cape-card.png';
import nurroCelebrate from '../../static/nurro/nurro-celebrate.png';
import nurroCelebrate2 from '../../static/nurro/nurro-celebrate2.png';
import nurroCelebrate3 from '../../static/nurro/nurro-celebrate3.png';
import nurroConfettiCard from '../../static/nurro/nurro-confetti-card.png';
import nurroConfetti from '../../static/nurro/nurro-confetti.png';
import nurroDance from '../../static/nurro/nurro-dance.png';
import nurroExperimentRecap from '../../static/nurro/nurro-experiment-recap.png';
import nurroExperiment from '../../static/nurro/nurro-experiment.png';
import nurroInspector from '../../static/nurro/nurro-inspector.png';
import nurroLaserTeach from '../../static/nurro/nurro-laser-teach.png';
import nurroMicroscope from '../../static/nurro/nurro-microscope.png';
import nurroOriginal from '../../static/nurro/nurro-original.png';
import nurroPopcornCard from '../../static/nurro/nurro-popcorn-card.png';
import nurroPopcorn from '../../static/nurro/nurro-popcorn.png';
import nurroSuccess from '../../static/nurro/nurro-success.png';
import nurroSuperV2 from '../../static/nurro/nurro-super-v2.png';
import nurroTrophy from '../../static/nurro/nurro-trophy.png';
import nurroWheres from '../../static/nurro/nurro-wheres.png';

const props = withDefaults(defineProps<{
  show: boolean;
  /** 'download' (default) saves to disk; 'attach' uploads to Firebase Storage
   *  and emits the public URL via the `attached` event. */
  mode?: 'download' | 'attach';
  /** A window to keep out of a whole-screen picture: the one this dialog was
   *  opened from (the Cell Library's help form). A picture for a helper
   *  should show the view, not the form it was sent from (Celia 2026-10-07). */
  hideOpener?: string;
}>(), { mode: 'download' });
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'attached', payload: { url: string }): void;
}>();

/** Cloud Function that mints short-lived signed PUT URLs for screenshot
 *  uploads. Implementation lives in functions/ (Firebase project eyewire-ii-e4d52) (see firebase-screenshot-
 *  upload-draft.md). Update this endpoint after deploying. */
// NOTE: the signScreenshotUpload Cloud Function is no longer used. It minted a
// v4 signed upload URL, which needs the runtime service account to sign via the
// IAM signBlob API — a permission it never had, so it returned 500 on every
// request. Uploads now go directly to Supabase Storage (see uploadBlob).

// The preview is sized to fill the stage (the area under the pen toolbar) at
// the OUTPUT aspect, so what you see is the frame you will get. Its backing
// store is the CSS size times devicePixelRatio (capped at 2) so the image and
// the pen marks stay sharp at the larger size.
const MAX_DPR = 2;
const STAGE_PAD = 22;   // room around the frame for the corner brackets

const PRESETS = [
  { label: '720p', w: 1280, h: 720 },
  { label: '1080p', w: 1920, h: 1080 },
  { label: '1440p', w: 2560, h: 1440 },
  { label: '4K', w: 3840, h: 2160 },
];

const width = ref(1920);
const height = ref(1080);
const transparent = ref(false);
const hideBoundingBox = ref(false);
const showScaleBar = ref(true);
/** Capture the whole tab, panels included, instead of the viewer canvas
 *  (Amy: a bug report needs the windows that were open). Uses the browser's
 *  screen capture of this tab, so it asks once and takes one frame. */
// Remembered (Amy 2026-09-29): "Whole screen" makes the browser ask to share
// the tab every time, and browsers never let a site save that permission.
// Untick it once and screenshots capture the viewer only, with no popup.
const WHOLE_SCREEN_KEY = 'nge_shot_whole_screen_v1';
/** The captured tab, once taken (whole screen mode). */
const screenCap = ref<SourceFrame | null>(null);
function retake() { screenCap.value = null; strokes.value = []; refreshSource(); }
const wholeScreen = ref((() => {
  try { const v = localStorage.getItem(WHOLE_SCREEN_KEY); if (v != null) return v === '1'; } catch { /* private mode */ }
  return props.mode === 'attach';
})());
watch(wholeScreen, v => { try { localStorage.setItem(WHOLE_SCREEN_KEY, v ? '1' : '0'); } catch { /* private mode */ } });
/** Crop to the 3D panel only (Amy), for clean neuron renders. */
const only3d = ref(false);
/** Nurro in a corner of the picture, because why not (Amy). Every
 *  transparent Nurro in static/nurro, in a fresh random order each time the
 *  dialog opens; "another" walks that shuffle, so none repeats until all
 *  have shown (Amy 2026-09-29). */
const NURROS_ALL = [nurroAtHome, nurroCapeCard, nurroCelebrate, nurroCelebrate2, nurroCelebrate3, nurroConfettiCard, nurroConfetti, nurroDance, nurroExperimentRecap, nurroExperiment, nurroInspector, nurroLaserTeach, nurroMicroscope, nurroOriginal, nurroPopcornCard, nurroPopcorn, nurroSuccess, nurroSuperV2, nurroTrophy, nurroWheres];
const NURROS = (() => {
  const a = NURROS_ALL.slice();
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
})();
const nurroOn = ref(false);
const nurroIndex = ref(0);
const nurroImg = ref<HTMLImageElement | null>(null);
const nurroSrc = computed(() => NURROS[nurroIndex.value]);
function loadNurro() {
  if (!nurroOn.value) { nurroImg.value = null; renderPreview(); return; }
  const img = new Image();
  const src = NURROS[nurroIndex.value];
  img.onload = () => { if (nurroOn.value && src === NURROS[nurroIndex.value]) { nurroImg.value = img; renderPreview(); } };
  img.src = src;
}
function nextNurro() {
  nurroIndex.value = (nurroIndex.value + 1) % NURROS.length;
  if (!nurroOn.value) nurroOn.value = true;
  loadNurro();
}
watch(nurroOn, loadNurro);
/** Bottom right corner, a quarter of the image height, with a margin. */
function drawNurro(ctx: CanvasRenderingContext2D, rect: { x: number; y: number; w: number; h: number }) {
  const img = nurroImg.value;
  if (!img || !img.naturalWidth) return;
  const h = Math.round(rect.h * 0.28);
  const w = Math.round(h * img.naturalWidth / img.naturalHeight);
  const m = Math.round(rect.h * 0.02);
  ctx.drawImage(img, rect.x + rect.w - w - m, rect.y + rect.h - h - m, w, h);
}
const busy = ref(false);
const errorMsg = ref('');
const dialogHidden = ref(false);
/** Takes the opener (and the layer it sits in) out of sight for the capture. */
let openerHidden: { el: HTMLElement; was: string }[] = [];
function hideOpener() {
  showOpener();
  if (!props.hideOpener) return;
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(props.hideOpener))) {
    const host = el.parentElement && el.parentElement !== document.body ? el.parentElement : el;
    openerHidden.push({ el: host, was: host.style.visibility });
    host.style.visibility = 'hidden';
  }
}
function showOpener() {
  for (const h of openerHidden) h.el.style.visibility = h.was;
  openerHidden = [];
}

// While the page is being drawn the dialog is hidden, so for a second or
// two nothing said a capture was under way (Ames 2026-10-06, Bug Report).
// A small sign with the growing cell sits in the middle of the screen; it is
// marked to be left out of the picture itself.
function showCaptureSign(): () => void {
  const el = document.createElement('div');
  el.className = 'nge-shot-sign';
  el.setAttribute(CAPTURE_SKIP, '');
  el.setAttribute('role', 'status');
  el.innerHTML = '<span class="nge-shot-sign-cell"></span><b>Capturing your screen</b>';
  document.body.appendChild(el);
  const app = createApp(GrowingCell, { size: 120, named: false });
  app.mount(el.querySelector('.nge-shot-sign-cell')!);
  return () => { try { app.unmount(); } catch { /* already gone */ } el.remove(); };
}

/** Drawing the page failed once since this dialog opened. The next press of
 *  Capture screen goes straight to the browser's own capture. */
let drawFailed = false;

async function captureWholeScreen(): Promise<HTMLCanvasElement> {
  if (!drawFailed) {
  // First choice: the app draws the page itself, so the browser has nothing
  // to ask (Ames 2026-10-01: "avoid the chrome permission every time").
  dialogHidden.value = true;
  document.body.classList.add('nge-shot-capturing');
  hideOpener();
  const hideSign = showCaptureSign();
  try {
    await new Promise(r => setTimeout(r, 60));
    return await capturePage();
  } catch (e) {
    console.warn('[screenshot] drawing the page failed, asking the browser to capture the tab instead:', e);
    drawFailed = true;
  } finally {
    hideSign();
    dialogHidden.value = false;
    document.body.classList.remove('nge-shot-capturing');
    showOpener();
  }
  }
  // The browser asks only straight after a click. A slow page drawing used up
  // that moment, and the capture failed with "getDisplayMedia requires
  // transient activation" (toknow, report 215). Ask for a fresh click.
  const ua = (navigator as any).userActivation;
  if (ua && !ua.isActive) {
    throw new Error('Drawing the page took too long. Press Capture screen and choose this tab.');
  }
  const md = navigator.mediaDevices as any;
  if (!md?.getDisplayMedia) throw new Error('This browser cannot capture the screen. Untick "Whole screen" to capture the viewer only.');
  // Hide this dialog so it is not in the picture, and the blurred pop-up that
  // opened it (Submit an issue): Celia's bug report attached a picture of the
  // report form over a blurred viewer. Every other window on screen (Cell
  // Library, chat, layer panels) stays in the shot (Ames 2026-09-28).
  dialogHidden.value = true;
  document.body.classList.add('nge-shot-capturing');
  hideOpener();
  await new Promise(r => setTimeout(r, 120));
  let stream: MediaStream | null = null;
  try {
    // Inside the try: a dismissed picker must still bring the dialog back.
    stream = await md.getDisplayMedia({
      video: { displaySurface: 'browser' }, audio: false,
      preferCurrentTab: true, selfBrowserSurface: 'include', surfaceSwitching: 'exclude',
    }) as MediaStream;
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // Let the picker's own overlay fade before grabbing a frame.
    await new Promise(r => setTimeout(r, 400));
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext('2d')!.drawImage(video, 0, 0);
    return c;
  } finally {
    stream?.getTracks().forEach(t => t.stop());
    dialogHidden.value = false;
    document.body.classList.remove('nge-shot-capturing');
    showOpener();
  }
}

// Captured source frame (with toggles applied) — kept in 2D canvas form,
// alongside the per-source-pixel physical size in nanometers (computed at
// capture time so preview and download draw identical scale bars).
type SourceFrame = { canvas: HTMLCanvasElement; sw: number; sh: number; nmPerPx: number | null };
const sourceRef = ref<SourceFrame | null>(null);
// Image rect within the preview canvas after letterboxing, in BACKING pixels
// of the preview canvas (not CSS pixels).
const imgRect = ref({ x: 0, y: 0, w: 1, h: 1 });

const previewEl = ref<HTMLCanvasElement | null>(null);
const markupEl = ref<HTMLCanvasElement | null>(null);
const stageEl = ref<HTMLDivElement | null>(null);
// CSS size of the preview frame, fitted into the stage at the output aspect.
const frameCss = ref({ w: 0, h: 0 });

// Pen state. Strokes store points in 0..1 range relative to imgRect, and
// the stroke width as a fraction of the image height, so they survive a
// dialog resize and render at the same relative weight at any output
// resolution (the preview is WYSIWYG for the downloaded PNG).
type Stroke = { color: string; size: number; points: { u: number; v: number }[];
  /** What the mark is (Celia, report 173, 2026-10-19). A pen stroke when
   *  absent. Shapes keep two points, the corners of the box they were dragged
   *  across (an arrow: tail, then head). Text keeps one point, its top left,
   *  and its size is the letter height as a fraction of the image height. */
  kind?: Tool; text?: string };
const strokes = ref<Stroke[]>([]);
/** Pen, outlined shapes, arrow and text; outlines only (Ames 2026-10-19). */
type Tool = 'pen' | 'rect' | 'ellipse' | 'arrow' | 'text';
const tool = ref<Tool>('pen');
const TOOLS: { id: Tool; label: string; title: string; icon: string }[] = [
  { id: 'pen', label: 'Pen', title: 'Draw freehand', icon: '<path d="M4 20l4-1 11-11-3-3L5 16z"/><path d="M14 6l3 3"/>' },
  { id: 'rect', label: 'Box', title: 'Rectangle: drag across. Shift for a square.', icon: '<rect x="4" y="6" width="16" height="12" rx="1.5"/>' },
  { id: 'ellipse', label: 'Circle', title: 'Circle: drag across. Shift for a perfect circle.', icon: '<ellipse cx="12" cy="12" rx="8" ry="6.5"/>' },
  { id: 'arrow', label: 'Arrow', title: 'Arrow: drag from the tail to the point.', icon: '<path d="M5 19L19 5"/><path d="M10 5h9v9"/>' },
  { id: 'text', label: 'Text', title: 'Text: click where it goes, type, press Enter.', icon: '<path d="M5 6V4h14v2"/><path d="M12 4v16"/><path d="M9 20h6"/>' },
];
/** Letter height on screen for a Size setting: 1 to 12 gives 13 to 46 px. */
const textPx = (size: number) => 10 + size * 3;
/** The text being typed: where it sits on screen and in the image. */
const typing = ref<{ x: number; y: number; uv: { u: number; v: number }; value: string } | null>(null);
const typingEl = ref<HTMLInputElement | null>(null);
const PEN_COLORS = ['#ff4d4d', '#ffd24d', '#5be3ff', '#ffffff'];
const penColor = ref(PEN_COLORS[0]);
/** Text has a colour of its own, white to start (Ames 2026-10-19). The
 *  swatches set the colour of the tool in hand. */
const textColor = ref('#ffffff');
const activeColor = computed(() => tool.value === 'text' ? textColor.value : penColor.value);
function setColor(c: string) {
  if (tool.value === 'text') textColor.value = c; else penColor.value = c;
  if (typing.value) void nextTick(() => typingEl.value?.focus());
}
/** Any colour, from the browser's picker (Amy 2026-09-29). */
const customColor = ref('#b388ff');
const customActive = computed(() => !PEN_COLORS.includes(activeColor.value));
function pickCustom(e: Event) {
  customColor.value = (e.target as HTMLInputElement).value;
  setColor(customColor.value);
}
const penSize = ref(3);
let drawing = false;

function close() {
  if (busy.value) return;
  errorMsg.value = '';
  emit('close');
}

function preset(w: number, h: number) {
  width.value = w;
  height.value = h;
}
function isPreset(w: number, h: number) {
  return width.value === w && height.value === h;
}

function previewDpr() {
  return Math.min(MAX_DPR, Math.max(1, window.devicePixelRatio || 1));
}

/** Aspect of the requested output, falling back to 16:9 while the width or
 *  height field is empty or invalid mid edit. */
function outputAspect(): number {
  // Whole screen: the picture is the tab, so the frame takes its shape.
  if (wholeScreen.value) {
    const c = screenCap.value;
    const a = c ? c.sw / c.sh : window.innerWidth / Math.max(1, window.innerHeight);
    if (a > 0 && isFinite(a)) return clamp(a, 0.1, 10);
  }
  const w = Number(width.value);
  const h = Number(height.value);
  if (w > 0 && h > 0 && isFinite(w / h)) return clamp(w / h, 0.1, 10);
  return 16 / 9;
}

/** Fit the preview frame into the stage at the output aspect. Returns true
 *  when the frame size changed (the canvases then need a redraw). */
function layoutFrame(): boolean {
  const st = stageEl.value;
  if (!st) return false;
  const availW = Math.max(0, st.clientWidth - STAGE_PAD * 2);
  const availH = Math.max(0, st.clientHeight - STAGE_PAD * 2);
  const aspect = outputAspect();
  let w = availW;
  let h = w / aspect;
  if (h > availH) {
    h = availH;
    w = h * aspect;
  }
  const next = { w: Math.max(0, Math.floor(w)), h: Math.max(0, Math.floor(h)) };
  const prev = frameCss.value;
  if (prev.w === next.w && prev.h === next.h) return false;
  frameCss.value = next;
  return true;
}

let relayoutRaf = 0;
function scheduleRelayout() {
  if (relayoutRaf) return;
  relayoutRaf = requestAnimationFrame(() => {
    relayoutRaf = 0;
    if (!props.show) return;
    if (layoutFrame()) renderPreview();
  });
}

/** Read viewer's current zoom-per-pixel and convert to nanometers per source
 *  canvas pixel. Mirrors what neuroglancer's perspective_view/panel.ts does:
 *  physicalSizePerPixel = zoomFactor / canvasHeight (in canonical base units,
 *  meters), so multiply by 1e9 to land in nm. Falls back to the slice-view
 *  navigation state for layouts without a perspective panel. */
/** The 3D panel's element: the only rendered panel that carries the
 *  "show slice views" checkbox. */
function perspectivePanelEl(): HTMLElement | null {
  return (document.querySelector('.perspective-panel-show-slice-views')?.closest('.neuroglancer-rendered-data-panel') as HTMLElement | null) ?? null;
}

/** Nanometres per SOURCE pixel in the 3D view, the way neuroglancer's own
 *  perspective scale bar computes it: zoomFactor is canonical voxels per
 *  panel height, times the canonical voxel's physical size. The old
 *  version divided by the whole canvas height and assumed metres, which
 *  printed things like "10000 m". */
function computeNmPerPx(viewer: any, sh: number): number | null {
  if (!sh) return null;
  const persp = viewer?.perspectiveNavigationState ?? viewer?.navigationState;
  const zoom = persp?.zoomFactor?.value;
  if (typeof zoom !== 'number' || !isFinite(zoom) || zoom <= 0) return null;
  const voxelM = viewer?.navigationState?.displayDimensionRenderInfo?.value?.canonicalVoxelPhysicalSize;
  if (typeof voxelM !== 'number' || !isFinite(voxelM) || voxelM <= 0) return null;
  const panel = perspectivePanelEl();
  const canvas: HTMLCanvasElement | undefined = viewer?.display?.canvas;
  const cssHeight = panel?.clientHeight || canvas?.clientHeight || 0;
  if (!cssHeight) return null;
  // Source pixels per CSS pixel (device pixel ratio of the WebGL canvas).
  const dpr = canvas && canvas.clientHeight ? canvas.height / canvas.clientHeight : 1;
  return (zoom / (cssHeight * dpr)) * voxelM * 1e9;
}

/** Capture a 2D canvas of the viewer's WebGL output with the given toggles
 *  applied. Restores viewer state synchronously after capturing — the user
 *  may see one frame of the override state before the next paint, which is
 *  acceptable because the dialog overlay covers most of the viewport. */
function captureSource(opts: { hideBoundingBox: boolean; showScaleBar: boolean }):
    SourceFrame | null {
  const viewer: any = (window as any)['viewer'];
  const sourceCanvas: HTMLCanvasElement | undefined = viewer?.display?.canvas;
  if (!sourceCanvas) return null;
  const sw = sourceCanvas.width;
  const sh = sourceCanvas.height;
  if (!sw || !sh) return null;

  const prevDefAnn = viewer.showDefaultAnnotations?.value;
  const prevScale = viewer.showScaleBar?.value;
  let touchedDefAnn = false;
  let touchedScale = false;

  try {
    if (opts.hideBoundingBox && prevDefAnn !== false) {
      viewer.showDefaultAnnotations.value = false;
      touchedDefAnn = true;
    }
    // We draw a custom overlay regardless, but flip ng's native flag too so
    // slice-view layouts honor "off" (otherwise ng would still render a
    // native bar inside slice panels).
    if (typeof prevScale === 'boolean' && opts.showScaleBar !== prevScale) {
      viewer.showScaleBar.value = opts.showScaleBar;
      touchedScale = true;
    }

    if (typeof viewer.display.draw === 'function') {
      viewer.display.draw();
    }
    const out = document.createElement('canvas');
    // 3D only: crop the source to the perspective panel's box.
    let crop = { x: 0, y: 0, w: sw, h: sh };
    if (only3d.value) {
      const panel = perspectivePanelEl();
      if (panel && sourceCanvas.clientWidth && sourceCanvas.clientHeight) {
        const cr = sourceCanvas.getBoundingClientRect();
        const pr = panel.getBoundingClientRect();
        const kx = sw / cr.width, ky = sh / cr.height;
        crop = {
          x: Math.max(0, Math.round((pr.left - cr.left) * kx)),
          y: Math.max(0, Math.round((pr.top - cr.top) * ky)),
          w: Math.min(sw, Math.round(pr.width * kx)),
          h: Math.min(sh, Math.round(pr.height * ky)),
        };
      }
    }
    out.width = crop.w;
    out.height = crop.h;
    const ctx = out.getContext('2d')!;
    ctx.drawImage(sourceCanvas, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
    return { canvas: out, sw: crop.w, sh: crop.h, nmPerPx: computeNmPerPx(viewer, sh) };
  } catch (e) {
    console.error('captureSource failed:', e);
    return null;
  } finally {
    if (touchedDefAnn) viewer.showDefaultAnnotations.value = prevDefAnn;
    if (touchedScale) viewer.showScaleBar.value = prevScale;
    if (typeof viewer.display.scheduleRedraw === 'function') {
      viewer.display.scheduleRedraw();
    }
  }
}

/** Pick a "nice" round physical length close to a target, using the same
 *  significand set neuroglancer's own scale bar uses. */
const ALLOWED_SIGNIFICANDS = [1, 1.5, 2, 3, 5, 7.5, 10];
function pickNicePhysicalLength(targetNm: number): number {
  if (targetNm <= 0 || !isFinite(targetNm)) return 1;
  const exponent = Math.floor(Math.log10(targetNm));
  const ten = 10 ** exponent;
  const targetSig = targetNm / ten;
  let bestSig = 1;
  for (const sig of ALLOWED_SIGNIFICANDS) {
    if (Math.abs(sig - targetSig) < Math.abs(bestSig - targetSig)) bestSig = sig;
  }
  return bestSig * ten;
}

function formatPhysicalLabel(nm: number): string {
  if (nm >= 1e9) return `${stripTrailingZero(nm / 1e9)} m`;
  if (nm >= 1e6) return `${stripTrailingZero(nm / 1e6)} mm`;
  if (nm >= 1e3) return `${stripTrailingZero(nm / 1e3)} µm`;
  if (nm >= 1) return `${stripTrailingZero(nm)} nm`;
  return `${stripTrailingZero(nm * 1e3)} pm`;
}
function stripTrailingZero(n: number): string {
  return Number(n.toFixed(3)).toString();
}

/** Draw a scale bar at the bottom-right of the given image rect in `ctx`.
 *  Sizes scale with rect.h so it stays readable from preview through 4K. */
function drawScaleBarOverlay(
    ctx: CanvasRenderingContext2D,
    rect: { x: number; y: number; w: number; h: number },
    nmPerSourcePx: number,
    sourceH: number) {
  const s = rect.h / sourceH;                // source-px → target-px
  const fontSize = clamp(Math.round(rect.h * 0.024), 11, 56);
  const barH     = clamp(Math.round(rect.h * 0.005), 2, 12);
  const margin   = clamp(Math.round(rect.h * 0.024), 8, 56);
  const innerPad = Math.max(6, Math.round(fontSize * 0.45));

  const targetTargetPx = rect.w * 0.18;
  const targetSourcePx = targetTargetPx / s;
  const targetNm = targetSourcePx * nmPerSourcePx;
  if (!(targetNm > 0) || !isFinite(targetNm)) return;

  const chosenNm = pickNicePhysicalLength(targetNm);
  const barTargetPx = (chosenNm / nmPerSourcePx) * s;
  const label = formatPhysicalLabel(chosenNm);

  ctx.save();
  ctx.font = `bold ${fontSize}px Arial, Helvetica, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const tw = ctx.measureText(label).width;
  const blockW = Math.max(barTargetPx, tw) + innerPad * 2;
  const blockH = fontSize + barH + innerPad * 1.6;
  const blockX = rect.x + rect.w - margin - blockW;
  const blockY = rect.y + rect.h - margin - blockH;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(blockX, blockY, blockW, blockH);

  const barX = blockX + (blockW - barTargetPx) / 2;
  const barY = blockY + blockH - innerPad - barH;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(barX, barY, barTargetPx, barH);
  ctx.fillText(label, blockX + blockW / 2, barY - Math.round(fontSize * 0.3));

  ctx.restore();
}
function clamp(v: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, v)); }

/** Render the captured source onto the preview canvas, letterboxed.
 *  Updates imgRect so markup coordinates can be mapped correctly. */
function renderPreview() {
  const cv = previewEl.value;
  if (!cv) return;
  const dpr = previewDpr();
  const PW = Math.max(1, Math.round(frameCss.value.w * dpr));
  const PH = Math.max(1, Math.round(frameCss.value.h * dpr));
  // Assigning width/height always clears the canvas, which is fine: the whole
  // frame is redrawn below.
  cv.width = PW;
  cv.height = PH;
  const ctx = cv.getContext('2d')!;

  if (transparent.value) {
    drawCheckerboard(ctx, PW, PH, Math.round(12 * dpr));
  } else {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, PW, PH);
  }

  const src = sourceRef.value;
  if (!src) {
    imgRect.value = { x: 0, y: 0, w: PW, h: PH };
    renderMarkup();
    return;
  }

  const { sw, sh, canvas: srcCanvas } = src;
  const sourceAspect = sw / sh;
  const targetAspect = PW / PH;
  let dw = PW, dh = PH, dx = 0, dy = 0;
  if (sourceAspect > targetAspect) {
    dh = Math.round(PW / sourceAspect);
    dy = Math.round((PH - dh) / 2);
  } else {
    dw = Math.round(PH * sourceAspect);
    dx = Math.round((PW - dw) / 2);
  }
  imgRect.value = { x: dx, y: dy, w: dw, h: dh };

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(srcCanvas, dx, dy, dw, dh);

  if (transparent.value) {
    try {
      const img = ctx.getImageData(dx, dy, dw, dh);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i] < 8 && d[i + 1] < 8 && d[i + 2] < 8) d[i + 3] = 0;
      }
      ctx.putImageData(img, dx, dy);
    } catch (e) {
      console.warn('Preview transparency pass failed:', e);
    }
  }

  if (showScaleBar.value && src.nmPerPx) {
    drawScaleBarOverlay(ctx, imgRect.value, src.nmPerPx, sh);
  }
  drawNurro(ctx, imgRect.value);

  renderMarkup();
}

function drawCheckerboard(ctx: CanvasRenderingContext2D, w: number, h: number, tile = 12) {
  for (let y = 0; y < h; y += tile) {
    for (let x = 0; x < w; x += tile) {
      const dark = ((x / tile) + (y / tile)) % 2 === 0;
      ctx.fillStyle = dark ? '#2a2f3a' : '#3a414f';
      ctx.fillRect(x, y, tile, tile);
    }
  }
}

/** Render strokes onto a target context, given the image rect on that
 *  target. Stroke points are in 0..1 of the image rect and stroke size is a
 *  fraction of the image height, so the same call serves the preview and
 *  the full resolution output. */
function renderStrokes(ctx: CanvasRenderingContext2D, rect: { x: number; y: number; w: number; h: number }) {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of strokes.value) {
    if (s.points.length === 0) continue;
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    if (s.kind && s.kind !== 'pen') { renderShape(ctx, rect, s); continue; }
    ctx.lineWidth = Math.max(1, s.size * rect.h);
    if (s.points.length === 1) {
      const p = s.points[0];
      const x = rect.x + p.u * rect.w;
      const y = rect.y + p.v * rect.h;
      ctx.beginPath();
      ctx.arc(x, y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    for (let i = 0; i < s.points.length; i++) {
      const p = s.points[i];
      const x = rect.x + p.u * rect.w;
      const y = rect.y + p.v * rect.h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

/** A shape or a line of text, on the same image rect as the pen strokes. */
function renderShape(ctx: CanvasRenderingContext2D, rect: { x: number; y: number; w: number; h: number }, s: Stroke) {
  const at = (p: { u: number; v: number }) => [rect.x + p.u * rect.w, rect.y + p.v * rect.h];
  if (s.kind === 'text') {
    const [x, y] = at(s.points[0]);
    const px = Math.max(8, s.size * rect.h);
    ctx.font = `600 ${px}px Inter, Roboto, system-ui, sans-serif`;
    ctx.textBaseline = 'top';
    // A dark edge keeps the letters readable over the EM, light or dark.
    ctx.lineWidth = Math.max(2, px / 5);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.strokeText(s.text || '', x, y);
    ctx.fillText(s.text || '', x, y);
    return;
  }
  if (s.points.length < 2) return;
  const [x0, y0] = at(s.points[0]);
  const [x1, y1] = at(s.points[s.points.length - 1]);
  ctx.lineWidth = Math.max(1, s.size * rect.h);
  ctx.beginPath();
  if (s.kind === 'rect') {
    ctx.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
  } else if (s.kind === 'ellipse') {
    ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, Math.abs(x1 - x0) / 2, Math.abs(y1 - y0) / 2, 0, 0, Math.PI * 2);
  } else if (s.kind === 'arrow') {
    const a = Math.atan2(y1 - y0, x1 - x0);
    const head = Math.max(10, ctx.lineWidth * 4);
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    for (const side of [-1, 1]) {
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - head * Math.cos(a + side * 0.5), y1 - head * Math.sin(a + side * 0.5));
    }
  }
  ctx.stroke();
}

function renderMarkup() {
  const cv = markupEl.value;
  if (!cv) return;
  // The markup layer shares the preview's backing size, so imgRect (in
  // preview backing pixels) applies to it unchanged.
  const pv = previewEl.value;
  const W = pv ? pv.width : cv.width;
  const H = pv ? pv.height : cv.height;
  if (cv.width !== W) cv.width = W;
  if (cv.height !== H) cv.height = H;
  const ctx = cv.getContext('2d')!;
  ctx.clearRect(0, 0, W, H);
  renderStrokes(ctx, imgRect.value);
}

/** Map a pointer event to 0..1 image coordinates. The pointer is converted
 *  from CSS pixels to the canvas's own backing pixels using the element's
 *  on screen box, so it is correct at any preview size and any DPR. */
function pointerToImgUV(e: PointerEvent): { u: number; v: number } | null {
  const cv = markupEl.value;
  if (!cv) return null;
  const rect = cv.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const x = ((e.clientX - rect.left) / rect.width) * cv.width;
  const y = ((e.clientY - rect.top) / rect.height) * cv.height;
  const r = imgRect.value;
  if (x < r.x || x > r.x + r.w || y < r.y || y > r.y + r.h) return null;
  return { u: (x - r.x) / r.w, v: (y - r.y) / r.h };
}

function onPointerDown(e: PointerEvent) {
  const uv = pointerToImgUV(e);
  if (!uv) return;
  // penSize is in CSS pixels on screen; store it as a fraction of the image
  // height (imgRect.h is in backing pixels, hence the DPR factor).
  const imgCssH = imgRect.value.h / previewDpr();
  if (tool.value === 'text') {
    // A line of text being typed elsewhere is kept first.
    commitTyping();
    e.preventDefault();
    // Text already there is picked up and moved instead.
    const hit = textAt(e);
    if (hit) {
      movingText = { mark: hit, dx: uv.u - hit.points[0].u, dy: uv.v - hit.points[0].v };
      (e.target as Element).setPointerCapture?.(e.pointerId);
      return;
    }
    // Placed in the picture's own box (the dialog is transformed, so screen
    // coordinates put it somewhere else, Ames 2026-10-19).
    const box = (e.target as HTMLElement).getBoundingClientRect();
    typing.value = { x: e.clientX - box.left, y: e.clientY - box.top, uv, value: '' };
    void nextTick(() => typingEl.value?.focus());
    return;
  }
  drawing = true;
  (e.target as Element).setPointerCapture?.(e.pointerId);
  const sizeFrac = penSize.value / Math.max(1, imgCssH);
  const kind = tool.value === 'pen' ? undefined : tool.value;
  strokes.value.push({ color: penColor.value, size: sizeFrac, points: kind ? [uv, uv] : [uv], kind });
  renderMarkup();
}
/** The text mark under the pointer, topmost first, or null. */
function textAt(e: PointerEvent): Stroke | null {
  const cv = markupEl.value;
  if (!cv) return null;
  const box = cv.getBoundingClientRect();
  const x = ((e.clientX - box.left) / box.width) * cv.width;
  const y = ((e.clientY - box.top) / box.height) * cv.height;
  const r = imgRect.value, ctx = cv.getContext('2d')!;
  for (let i = strokes.value.length - 1; i >= 0; i--) {
    const m = strokes.value[i];
    if (m.kind !== 'text') continue;
    const px = Math.max(8, m.size * r.h);
    ctx.font = `600 ${px}px Inter, Roboto, system-ui, sans-serif`;
    const w = ctx.measureText(m.text || '').width;
    const x0 = r.x + m.points[0].u * r.w, y0 = r.y + m.points[0].v * r.h;
    const pad = px * 0.25;
    if (x >= x0 - pad && x <= x0 + w + pad && y >= y0 - pad && y <= y0 + px * 1.2 + pad) return m;
  }
  return null;
}
/** A text mark being dragged, and where in it it was picked up. */
let movingText: { mark: Stroke; dx: number; dy: number } | null = null;

function onPointerMove(e: PointerEvent) {
  if (movingText) {
    const cv = markupEl.value, r = imgRect.value;
    if (!cv) return;
    const box = cv.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * cv.width;
    const y = ((e.clientY - box.top) / box.height) * cv.height;
    movingText.mark.points[0] = { u: clamp((x - r.x) / r.w - movingText.dx, 0, 1), v: clamp((y - r.y) / r.h - movingText.dy, 0, 1) };
    renderMarkup();
    return;
  }
  if (tool.value === 'text' && markupEl.value) {
    // A hand over text that can be moved.
    markupEl.value.style.cursor = textAt(e) ? 'move' : '';
  }
  if (!drawing) return;
  const cur = strokes.value[strokes.value.length - 1];
  if (!cur) return;
  if (cur.kind && cur.kind !== 'pen') {
    // A shape follows the pointer even past the image's edge, clamped to it.
    const cv = markupEl.value, r = imgRect.value;
    if (!cv) return;
    const box = cv.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * cv.width;
    const y = ((e.clientY - box.top) / box.height) * cv.height;
    let u = clamp((x - r.x) / r.w, 0, 1), v = clamp((y - r.y) / r.h, 0, 1);
    if (e.shiftKey && cur.kind !== 'arrow') {
      // Shift: a square or a circle (equal sides in image pixels).
      const p0 = cur.points[0];
      const side = Math.max(Math.abs(u - p0.u) * r.w, Math.abs(v - p0.v) * r.h);
      u = clamp(p0.u + Math.sign(u - p0.u || 1) * side / r.w, 0, 1);
      v = clamp(p0.v + Math.sign(v - p0.v || 1) * side / r.h, 0, 1);
    }
    cur.points[1] = { u, v };
    renderMarkup();
    return;
  }
  const uv = pointerToImgUV(e);
  if (!uv) return;
  cur.points.push(uv);
  renderMarkup();
}
/** Keep the text being typed as a mark (empty text is dropped). */
function commitTyping() {
  const t = typing.value;
  typing.value = null;
  const text = (t?.value || '').trim();
  if (!t || !text) return;
  const imgCssH = imgRect.value.h / previewDpr();
  strokes.value.push({ kind: 'text', text: text.slice(0, 200), color: textColor.value,
    size: textPx(penSize.value) / Math.max(1, imgCssH), points: [t.uv] });
  renderMarkup();
}
function onTypingKey(e: KeyboardEvent) {
  if (e.key === 'Enter') { e.preventDefault(); commitTyping(); }
  else if (e.key === 'Escape') { e.preventDefault(); typing.value = null; }
}
function onPointerUp(e: PointerEvent) {
  drawing = false;
  movingText = null;
  (e.target as Element).releasePointerCapture?.(e.pointerId);
}

function undo() {
  typing.value = null;
  strokes.value.pop();
  renderMarkup();
}
function clearMarkup() {
  typing.value = null;
  strokes.value = [];
  renderMarkup();
}

async function refreshSource() {
  errorMsg.value = '';
  // Whole screen: there is nothing to show until the tab has been captured.
  // The viewer's own picture is NOT what gets attached, and it was showing
  // here as a black box that looked broken (Ames 2026-10-01).
  if (wholeScreen.value) {
    sourceRef.value = screenCap.value;
    await nextTick();
    renderPreview();
    return;
  }
  const cap = captureSource({
    hideBoundingBox: hideBoundingBox.value,
    showScaleBar: showScaleBar.value,
  });
  if (!cap) {
    errorMsg.value = 'Viewer not ready. Try again in a moment.';
    sourceRef.value = null;
  } else {
    sourceRef.value = cap;
  }
  await nextTick();
  renderPreview();
}

watch(() => props.show, async (open) => {
  if (open) {
    // Stay out of sight until the whole-screen picture is taken (no flash).
    if (wholeScreen.value) dialogHidden.value = true;
    await nextTick();
    const viewer: any = (window as any)['viewer'];
    if (typeof viewer?.showScaleBar?.value === 'boolean') {
      showScaleBar.value = viewer.showScaleBar.value;
    }
    hideBoundingBox.value = false;
    transparent.value = false;
    only3d.value = false;
    nurroOn.value = false;
    nurroImg.value = null;
    strokes.value = [];
    screenCap.value = null;
    drawFailed = false;
    // Whole screen: take the picture straight away, while the click that
    // opened this still counts as permission to ask. The dialog then shows
    // once, with the real picture in it (Ames 2026-10-01: it used to show a
    // preview without the panels, then come back a second time after the
    // browser's prompt). If the prompt is dismissed, the dialog opens with a
    // Capture screen button instead.
    if (wholeScreen.value) {
      busy.value = true;
      try {
        const shot = await captureWholeScreen();
        screenCap.value = { canvas: shot, sw: shot.width, sh: shot.height, nmPerPx: null };
      } catch (e: any) {
        if (e?.name !== 'NotAllowedError') errorMsg.value = e?.message ?? String(e);
      } finally {
        busy.value = false;
      }
      await nextTick();
    }
    layoutFrame();
    const firstError = errorMsg.value;
    await refreshSource();
    if (firstError) errorMsg.value = firstError;
  }
});

watch(wholeScreen, () => {
  screenCap.value = null;
  strokes.value = [];
  if (!props.show) return;
  layoutFrame();
  refreshSource();
});

watch([hideBoundingBox, only3d], () => {
  if (props.show) refreshSource();
});
// Scale bar toggle is post-process, so no need to recapture, just redraw.
watch([showScaleBar, transparent], () => {
  if (props.show) renderPreview();
});
// The preview frame follows the output aspect.
watch([width, height], () => {
  if (props.show) scheduleRelayout();
});

// Refit the frame whenever the stage changes size (window resize, sidebar
// wrapping). The stage only exists while the dialog is open.
watch(stageEl, (el, _prev, onCleanup) => {
  if (!el || typeof ResizeObserver === 'undefined') return;
  const ro = new ResizeObserver(() => scheduleRelayout());
  ro.observe(el);
  onCleanup(() => ro.disconnect());
});

onBeforeUnmount(() => {
  drawing = false;
  if (relayoutRaf) cancelAnimationFrame(relayoutRaf);
  relayoutRaf = 0;
});

/** Upload a PNG blob to Supabase Storage. Returns its public URL. */
async function uploadBlob(blob: Blob): Promise<string> {
  // Upload straight to Supabase Storage.
  //
  // This used to POST to the signScreenshotUpload Cloud Function for a v4
  // signed URL and then PUT the blob to it. Signing requires the function's
  // runtime service account to call the IAM signBlob API, a permission it was
  // never granted, so every attach failed with "Sign URL failed (500)".
  // Supabase Storage needs no signing round trip, no extra IAM and no Cloud
  // Function — the bucket and policies already exist for admin uploads.
  const { useProofreadingBackendStore: useBackend } = await import('../store');
  return await useBackend().uploadHelpScreenshot(blob);
}

async function download() {
  errorMsg.value = '';
  if (wholeScreen.value) {
    busy.value = true;
    try {
      // Step one: take the picture and bring it back into the dialog, so it
      // can be looked at and drawn on. Step two (the same button) sends it.
      if (!screenCap.value) {
        const shot = await captureWholeScreen();
        screenCap.value = { canvas: shot, sw: shot.width, sh: shot.height, nmPerPx: null };
        strokes.value = [];
        await nextTick();
        layoutFrame();
        await refreshSource();
        return;
      }
      const shot = screenCap.value.canvas;
      const c = document.createElement('canvas');
      c.width = shot.width;
      c.height = shot.height;
      const full = { x: 0, y: 0, w: c.width, h: c.height };
      c.getContext('2d')!.drawImage(shot, 0, 0);
      drawNurro(c.getContext('2d')!, full);
      renderStrokes(c.getContext('2d')!, full);
      const blob: Blob = await new Promise((resolve, reject) => {
        c.toBlob(b => b ? resolve(b) : reject(new Error('toBlob returned null')), 'image/png');
      });
      if (props.mode === 'attach') {
        const publicUrl = await uploadBlob(blob);
        emit('attached', { url: publicUrl });
        emit('close');
        return;
      }
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eyewire-${ts}-screen.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      emit('close');
    } catch (e: any) {
      // A dismissed picker is not an error worth shouting about.
      if (e?.name !== 'NotAllowedError') errorMsg.value = e?.message ?? String(e);
    } finally {
      busy.value = false;
    }
    return;
  }
  const w = Math.max(16, Math.min(8192, Math.floor(width.value || 0)));
  const h = Math.max(16, Math.min(8192, Math.floor(height.value || 0)));
  if (!w || !h) {
    errorMsg.value = 'Width and height must be positive integers (max 8192).';
    return;
  }

  busy.value = true;
  try {
    const cap = captureSource({
      hideBoundingBox: hideBoundingBox.value,
      showScaleBar: showScaleBar.value,
    });
    if (!cap) throw new Error('Viewer not ready.');

    const { sw, sh, canvas: src, nmPerPx } = cap;
    const off = document.createElement('canvas');
    off.width = w;
    off.height = h;
    const ctx = off.getContext('2d')!;

    const sourceAspect = sw / sh;
    const targetAspect = w / h;
    let dw = w, dh = h, dx = 0, dy = 0;
    if (sourceAspect > targetAspect) {
      dh = Math.round(w / sourceAspect);
      dy = Math.round((h - dh) / 2);
    } else {
      dw = Math.round(h * sourceAspect);
      dx = Math.round((w - dw) / 2);
    }

    if (transparent.value) {
      ctx.clearRect(0, 0, w, h);
    } else {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, w, h);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, dx, dy, dw, dh);

    if (transparent.value) {
      try {
        const img = ctx.getImageData(dx, dy, dw, dh);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] < 8 && d[i + 1] < 8 && d[i + 2] < 8) d[i + 3] = 0;
        }
        ctx.putImageData(img, dx, dy);
      } catch (e) {
        console.warn('Transparency pass skipped:', e);
      }
    }

    const outRect = { x: dx, y: dy, w: dw, h: dh };
    if (showScaleBar.value && nmPerPx) {
      drawScaleBarOverlay(ctx, outRect, nmPerPx, sh);
    }
    drawNurro(ctx, outRect);

    // Markup at full output resolution. Points are 0..1 of the image rect and
    // sizes are a fraction of image height, so outRect alone places them.
    renderStrokes(ctx, outRect);

    const blob: Blob = await new Promise((resolve, reject) => {
      off.toBlob(b => b ? resolve(b) : reject(new Error('toBlob returned null')), 'image/png');
    });

    if (props.mode === 'attach') {
      const publicUrl = await uploadBlob(blob);
      emit('attached', { url: publicUrl });
      emit('close');
      return;
    }

    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eyewire-${ts}-${w}x${h}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30000);

    emit('close');
  } catch (e: any) {
    errorMsg.value = e?.message || 'Screenshot failed.';
    console.error('Screenshot error:', e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <!-- Teleported to <body> so it always sits above the panel that opened it
       (Cell Library help request, chat, Scout Tag); inside a panel it was
       trapped under that panel's stacking layer (Amy 2026-09-29). -->
  <Teleport to="body">
  <div v-if="show" class="nge-shotdlg-overlay" :style="dialogHidden ? 'visibility:hidden' : ''" @click.self="close">
    <div class="nge-shotdlg" role="dialog"
         :aria-label="props.mode === 'attach' ? 'Attach screenshot' : 'Save screenshot'">
      <div class="nge-shotdlg-header">
        <span class="material-symbols-outlined nge-shotdlg-ico">photo_camera</span>
        <span class="nge-shotdlg-title">{{ props.mode === 'attach' ? 'Attach screenshot' : 'Save screenshot' }}</span>
        <button class="nge-shotdlg-close" @click="close" aria-label="Close">×</button>
      </div>

      <div class="nge-shotdlg-main">
        <!-- Pen tools: a HUD strip across the top of the image. -->
        <div class="nge-shotdlg-toolbar" role="toolbar" aria-label="Drawing tools">
          <div class="nge-shotdlg-tools" role="radiogroup" aria-label="Tool">
            <button v-for="t in TOOLS" :key="t.id" type="button"
                    class="nge-shotdlg-tool nge-shotdlg-tool--pick" :class="{ 'is-active': tool === t.id }"
                    role="radio" :aria-checked="tool === t.id" :title="t.title"
                    @click="commitTyping(); tool = t.id">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                   stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" v-html="t.icon" />
              <span>{{ t.label }}</span>
            </button>
          </div>

          <span class="nge-shotdlg-sep" aria-hidden="true" />

          <span class="nge-shotdlg-tlabel">Colour</span>
          <div class="nge-shotdlg-swatches">
            <button v-for="c in PEN_COLORS" :key="c"
                    class="nge-shotdlg-swatch"
                    :class="{ 'is-active': activeColor === c }"
                    :style="{ '--sw': c }"
                    @click="setColor(c)"
                    :title="c"
                    :aria-label="`Pen color ${c}`"
                    :aria-pressed="activeColor === c" />
            <label class="nge-shotdlg-swatch nge-shotdlg-swatch--custom"
                   :class="{ 'is-active': customActive }"
                   :style="customActive ? { '--sw': activeColor } : {}"
                   title="Any colour">
              <input type="color" :value="customColor" @input="pickCustom" @click="setColor(customColor)"
                     aria-label="Pick any pen colour" />
            </label>
          </div>

          <span class="nge-shotdlg-sep" aria-hidden="true" />

          <span class="nge-shotdlg-tlabel">Size</span>
          <input type="range" min="1" max="12" step="1" v-model.number="penSize"
                 class="nge-shotdlg-pen-size" :title="tool === 'text' ? `Text size: ${textPx(penSize)}px` : `Line width: ${penSize}px`"
                 :aria-label="tool === 'text' ? 'Text size' : 'Line width'" />
          <span class="nge-shotdlg-readout nge-shotdlg-readout-size">{{ tool === 'text' ? textPx(penSize) : penSize }}<span class="nge-shotdlg-unit">px</span></span>

          <span class="nge-shotdlg-sep" aria-hidden="true" />

          <button class="nge-shotdlg-tool" @click="undo" :disabled="!strokes.length"
                  title="Undo last stroke">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 14l-4-4 4-4"/><path d="M5 10h9a5 5 0 0 1 0 10h-3"/>
            </svg>
            <span>Undo</span>
          </button>
          <button class="nge-shotdlg-tool" @click="clearMarkup" :disabled="!strokes.length"
                  title="Clear all marks">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/>
            </svg>
            <span>Clear</span>
          </button>

          <span class="nge-shotdlg-toolbar-fill" />
          <span class="nge-shotdlg-readout nge-shotdlg-readout-dim">Marks {{ strokes.length }}</span>
          <span v-if="sourceRef" class="nge-shotdlg-readout nge-shotdlg-readout-dim nge-shotdlg-readout-src">
            Src {{ sourceRef.sw }} × {{ sourceRef.sh }}<span class="nge-shotdlg-unit">px</span>
          </span>
        </div>

        <!-- Stage: the preview frame is fitted in here at the output aspect. -->
        <div ref="stageEl" class="nge-shotdlg-stage">
          <div class="nge-shotdlg-frame"
               :style="{ width: frameCss.w + 'px', height: frameCss.h + 'px' }">
            <div class="nge-shotdlg-clip">
              <canvas ref="previewEl" class="nge-shotdlg-preview" />
              <canvas ref="markupEl" class="nge-shotdlg-markup" :class="`nge-shotdlg-markup--${tool}`"
                      @pointerdown="onPointerDown"
                      @pointermove="onPointerMove"
                      @pointerup="onPointerUp"
                      @pointercancel="onPointerUp" />
              <!-- The text tool's box, where the click was; Enter keeps it. -->
              <input v-if="typing" ref="typingEl" v-model="typing.value" class="nge-shotdlg-typing"
                     :style="{ left: (typing.x - 3) + 'px', top: (typing.y - 1) + 'px', color: textColor, fontSize: textPx(penSize) + 'px' }"
                     maxlength="200" placeholder="Type, then Enter" aria-label="Text to add to the picture"
                     @keydown.stop="onTypingKey" @keyup.stop @keypress.stop @blur="commitTyping" />
              <div v-if="wholeScreen && !screenCap" class="nge-shotdlg-wait">
                <b>Whole screen capture</b>
                <span>Press Capture screen. The picture appears here, ready to draw on.</span>
              </div>
              <!-- The longest wait in the app: a cell grows while it runs, and
                   a long one cycles through the kinds (Ames 2026-10-06). Not
                   shown while a whole screen capture is being taken, when
                   the dialog itself is hidden. -->
              <div v-if="busy && (screenCap || !wholeScreen)" class="nge-shotdlg-wait nge-shotdlg-wait--busy" role="status">
                <GrowingCell :size="150" />
                <b>{{ props.mode === 'attach' ? 'Uploading' : 'Saving' }}</b>
              </div>
              <span class="nge-shotdlg-scan" aria-hidden="true" />
            </div>
            <i class="nge-shotdlg-brk tl" aria-hidden="true" />
            <i class="nge-shotdlg-brk tr" aria-hidden="true" />
            <i class="nge-shotdlg-brk bl" aria-hidden="true" />
            <i class="nge-shotdlg-brk br" aria-hidden="true" />
          </div>
        </div>
      </div>

      <!-- Right bar: output, options, actions. -->
      <div class="nge-shotdlg-side">
        <section class="nge-shotdlg-sec" v-if="props.mode !== 'attach'">
          <h3 class="nge-shotdlg-sechead">Output size</h3>
          <div class="nge-shotdlg-dims">
            <label class="nge-shotdlg-field">
              <span class="nge-shotdlg-flabel">Width</span>
              <input type="number" v-model.number="width" min="16" max="8192" />
            </label>
            <span class="nge-shotdlg-times">×</span>
            <label class="nge-shotdlg-field">
              <span class="nge-shotdlg-flabel">Height</span>
              <input type="number" v-model.number="height" min="16" max="8192" />
            </label>
            <span class="nge-shotdlg-px">px</span>
          </div>
          <div class="nge-shotdlg-presets" v-if="props.mode !== 'attach'">
            <button v-for="p in PRESETS" :key="p.label"
                    :class="{ 'is-active': isPreset(p.w, p.h) }"
                    @click="preset(p.w, p.h)">
              <b>{{ p.label }}</b>
              <span>{{ p.w }} × {{ p.h }}</span>
            </button>
          </div>
        </section>

        <section class="nge-shotdlg-sec">
          <h3 class="nge-shotdlg-sechead">Options</h3>
          <div class="nge-shotdlg-checks">
            <label class="nge-shotdlg-check" title="A picture of the page as you see it, open panels included.">
              <input type="checkbox" v-model="wholeScreen" />
              <span>Whole screen, with panels</span>
            </label>
            <label class="nge-shotdlg-check" :class="{ 'is-off': wholeScreen }">
              <input type="checkbox" v-model="transparent" :disabled="wholeScreen" />
              <span>Transparent background</span>
            </label>
            <label class="nge-shotdlg-check" :class="{ 'is-off': wholeScreen }">
              <input type="checkbox" v-model="hideBoundingBox" :disabled="wholeScreen" />
              <span>Hide volume edge / bounding box</span>
            </label>
            <label class="nge-shotdlg-check" :class="{ 'is-off': wholeScreen }">
              <input type="checkbox" v-model="showScaleBar" :disabled="wholeScreen" />
              <span>Show scale bar</span>
            </label>
            <label class="nge-shotdlg-check" :class="{ 'is-off': wholeScreen }" title="Crop to the 3D view">
              <input type="checkbox" v-model="only3d" :disabled="wholeScreen" />
              <span>3D view only</span>
            </label>
          </div>
          <div class="nge-shotdlg-nurro-row">
            <label class="nge-shotdlg-check" title="Put a Nurro in the bottom right corner of the picture">
              <input type="checkbox" v-model="nurroOn" />
              <span>Add a Nurro</span>
            </label>
            <img :src="nurroSrc" alt="" class="nge-shotdlg-nurro-thumb" :class="{ 'is-on': nurroOn }" @click="nextNurro" title="Click for another Nurro" />
            <button class="nge-shotdlg-nurro-next" @click="nextNurro" title="Another Nurro">another</button>
          </div>
        </section>

        <div v-if="errorMsg" class="nge-shotdlg-err">{{ errorMsg }}</div>

        <div class="nge-shotdlg-actions">
          <button class="nge-shotdlg-primary" @click="download" :disabled="busy">
            {{ busy
                ? (wholeScreen && !screenCap ? 'Capturing…' : props.mode === 'attach' ? 'Uploading…' : 'Rendering…')
                : (wholeScreen && !screenCap ? 'Capture screen' : props.mode === 'attach' ? 'Attach' : 'Download') }}
          </button>
          <button v-if="wholeScreen && screenCap" class="nge-shotdlg-cancel" @click="retake" :disabled="busy">Retake</button>
          <button class="nge-shotdlg-cancel" @click="close" :disabled="busy">Cancel</button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<style scoped>
.nge-shotdlg-nurro-row { display: flex; align-items: center; gap: 10px; margin-top: 6px; }
.nge-shotdlg-nurro-thumb {
  width: 40px; height: 40px; object-fit: contain; cursor: pointer;
  opacity: 0.45; transition: opacity 0.15s, transform 0.15s;
}
.nge-shotdlg-nurro-thumb.is-on { opacity: 1; }
.nge-shotdlg-nurro-thumb:hover { transform: scale(1.12); opacity: 1; }
.nge-shotdlg-nurro-next {
  padding: 4px 10px; border-radius: 6px;
  border: 1px solid rgba(245, 166, 35, 0.45); background: rgba(245, 166, 35, 0.12);
  color: #ffd27a; font: inherit; font-size: 0.8em; cursor: pointer;
}
.nge-shotdlg-nurro-next:hover { background: rgba(245, 166, 35, 0.24); }
.nge-shotdlg-check.is-off { opacity: 0.45; }
.nge-shotdlg-wait {
  position: absolute; inset: 0; z-index: 2;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  padding: 24px; text-align: center; pointer-events: none;
  color: rgba(190, 215, 245, 0.8); font-size: 14px; line-height: 1.5;
}
.nge-shotdlg-wait b { font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: #7fd6ff; font-weight: 600; }
.nge-shotdlg-wait span { max-width: 420px; }
.nge-shotdlg-wait--busy { background: rgba(2, 8, 18, 0.72); z-index: 3; }

/* Styled after Amy's scifi-ui library (holopanel surface, holoframe corner
   brackets, holoscan single pass), with the values copied inline rather than
   depending on the library at runtime. */
.nge-shotdlg-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 14, 0.62);
  z-index: 10040;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.nge-shotdlg {
  --sd-line: 196 228 255;
  --sd-glow: 74 158 255;
  --sd-cyan: 126 224 255;
  --sd-ink: 214 234 252;
  position: relative;
  isolation: isolate;
  width: min(1400px, 94vw);
  height: min(900px, 90vh);
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-areas:
    "head head"
    "main side";
  background: linear-gradient(158deg,
    rgba(14, 24, 44, 0.97) 0%,
    rgba(10, 18, 35, 0.98) 45%,
    rgba(6, 10, 18, 0.98) 100%);
  border: 1px solid rgb(var(--sd-glow) / 0.32);
  border-radius: 14px;
  color: rgb(var(--sd-ink));
  box-shadow:
    0 18px 50px rgba(0, 0, 0, 0.55),
    0 0 60px rgb(var(--sd-glow) / 0.08),
    inset 0 1px 0 rgb(var(--sd-line) / 0.10);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.35;
  overflow: hidden;
  animation: nge-shotdlg-in 720ms cubic-bezier(.16, 1, .3, 1) both;
}
/* the lit hairline along the top edge (holopanel::before) */
.nge-shotdlg::before {
  content: "";
  position: absolute;
  left: 8%;
  right: 8%;
  top: 0;
  height: 1px;
  pointer-events: none;
  z-index: 3;
  background: linear-gradient(90deg, transparent,
    rgb(var(--sd-line) / 0.95) 50%, transparent);
  box-shadow: 0 0 12px rgb(178 216 248 / 0.6);
}
@keyframes nge-shotdlg-in {
  0%   { opacity: 0; transform: translateY(10px) scale(.97);
         filter: blur(10px) brightness(2.5); }
  60%  { opacity: 1; transform: translateY(0) scale(1);
         filter: blur(0) brightness(1.15); }
  100% { opacity: 1; transform: none; filter: none; }
}

/* ---- header ---- */
.nge-shotdlg-header {
  grid-area: head;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 11px 18px;
  border-bottom: 1px solid rgb(var(--sd-glow) / 0.18);
}
.nge-shotdlg-ico { font-size: 19px; color: rgb(var(--sd-cyan)); }
.nge-shotdlg-title {
  font-family: 'Orbitron', 'Inter', sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgb(var(--sd-line));
}
.nge-shotdlg-close {
  font-family: inherit;
  margin-left: auto;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: rgb(var(--sd-ink) / 0.8);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
}
.nge-shotdlg-close:hover {
  color: #ffffff;
  border-color: rgb(var(--sd-glow) / 0.4);
  background: rgb(var(--sd-glow) / 0.1);
}

/* ---- main column: toolbar over stage ---- */
.nge-shotdlg-main {
  grid-area: main;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
}

.nge-shotdlg-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 9px 16px;
  background:
    repeating-linear-gradient(0deg, rgb(var(--sd-cyan) / 0.025) 0 1px, transparent 1px 3px),
    rgba(6, 12, 26, 0.72);
  border-bottom: 1px solid rgb(var(--sd-glow) / 0.16);
}
.nge-shotdlg-tlabel,
.nge-shotdlg-sechead,
.nge-shotdlg-flabel {
  font-family: 'Orbitron', 'Inter', sans-serif;
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgb(var(--sd-line) / 0.72);
}
.nge-shotdlg-sep {
  width: 1px;
  height: 22px;
  background: linear-gradient(180deg, transparent, rgb(var(--sd-glow) / 0.45), transparent);
}
.nge-shotdlg-toolbar-fill { flex: 1 1 auto; }

.nge-shotdlg-swatches {
  display: flex;
  gap: 7px;
  padding: 4px 6px;
  border: 1px solid rgb(var(--sd-glow) / 0.18);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.25);
}
.nge-shotdlg-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.22);
  background: var(--sw);
  cursor: pointer;
  padding: 0;
  transition: transform .12s, box-shadow .15s, border-color .15s;
}
.nge-shotdlg-swatch:hover { transform: scale(1.12); }
/* The rainbow swatch opens the native colour picker; once a custom colour is
   in use it shows that colour instead. */
.nge-shotdlg-swatch--custom {
  position: relative;
  overflow: hidden;
  background: conic-gradient(#ff4d4d, #ffd24d, #7dff7a, #5be3ff, #6a7bff, #d06bff, #ff4d4d);
}
.nge-shotdlg-swatch--custom.is-active { background: var(--sw); }
.nge-shotdlg-swatch--custom input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: 0;
  padding: 0;
}
.nge-shotdlg-swatch.is-active {
  border-color: #ffffff;
  box-shadow:
    0 0 0 2px rgba(6, 12, 26, 0.9),
    0 0 0 3.5px rgb(var(--sd-cyan) / 0.85),
    0 0 12px var(--sw);
}

.nge-shotdlg-pen-size {
  -webkit-appearance: none;
  appearance: none;
  width: 110px;
  height: 18px;
  background: transparent;
  cursor: pointer;
  margin: 0;
}
.nge-shotdlg-pen-size::-webkit-slider-runnable-track {
  height: 3px;
  border-radius: 2px;
  background:
    repeating-linear-gradient(90deg, rgb(var(--sd-line) / 0.35) 0 1px, transparent 1px 9px),
    rgb(var(--sd-glow) / 0.28);
}
.nge-shotdlg-pen-size::-moz-range-track {
  height: 3px;
  border-radius: 2px;
  background: rgb(var(--sd-glow) / 0.28);
}
.nge-shotdlg-pen-size::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  margin-top: -4.5px;
  border-radius: 50%;
  background: rgb(var(--sd-cyan));
  border: 2px solid rgba(6, 12, 26, 0.95);
  box-shadow: 0 0 8px rgb(var(--sd-cyan) / 0.7);
}
.nge-shotdlg-pen-size::-moz-range-thumb {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgb(var(--sd-cyan));
  border: 2px solid rgba(6, 12, 26, 0.95);
  box-shadow: 0 0 8px rgb(var(--sd-cyan) / 0.7);
}

.nge-shotdlg-readout {
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--sd-cyan) / 0.95);
  white-space: nowrap;
}
.nge-shotdlg-readout-size { min-width: 34px; }
.nge-shotdlg-readout-dim { color: rgb(var(--sd-line) / 0.5); }
/* unit symbols keep their case inside uppercase readouts */
.nge-shotdlg-unit {
  text-transform: none;
  margin-left: 2px;
}

.nge-shotdlg-tool {
  font-family: inherit;
  height: 30px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 11px;
  background: rgb(var(--sd-glow) / 0.08);
  border: 1px solid rgb(var(--sd-glow) / 0.3);
  border-radius: 6px;
  color: rgb(var(--sd-ink));
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, border-color .15s, color .15s, opacity .15s;
}
.nge-shotdlg-tool:hover:not(:disabled) {
  background: rgb(var(--sd-glow) / 0.2);
  border-color: rgb(var(--sd-cyan) / 0.6);
  color: #ffffff;
}
.nge-shotdlg-tool:disabled {
  opacity: 0.4;
  cursor: default;
}
/* the tool picker: one lit at a time */
.nge-shotdlg-tools { display: inline-flex; gap: 4px; }
.nge-shotdlg-tool--pick { padding: 0 9px; }
.nge-shotdlg-tool--pick.is-active {
  background: rgb(var(--sd-cyan) / 0.22);
  border-color: rgb(var(--sd-cyan) / 0.85);
  color: #ffffff;
  box-shadow: 0 0 10px rgb(var(--sd-cyan) / 0.25);
}
.nge-shotdlg-markup--rect, .nge-shotdlg-markup--ellipse, .nge-shotdlg-markup--arrow { cursor: crosshair; }
.nge-shotdlg-markup--text { cursor: text; }
/* the line of text being typed, over the picture where it will land */
.nge-shotdlg-typing {
  position: absolute; z-index: 5; min-width: 140px; margin: 0; padding: 0 2px; box-sizing: content-box;
  font-family: Inter, Roboto, system-ui, sans-serif; font-weight: 600; line-height: 1.15;
  background: rgba(4, 10, 20, 0.55); border: 1px dashed rgba(126, 224, 255, 0.7); border-radius: 3px;
  outline: none; text-shadow: 0 0 3px #000, 0 0 2px #000;
}
.nge-shotdlg-typing::placeholder { color: rgba(220, 235, 250, 0.55); text-shadow: none; }

/* ---- stage + frame ---- */
.nge-shotdlg-stage {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(ellipse at 50% 45%, rgb(var(--sd-glow) / 0.07), transparent 70%),
    linear-gradient(rgb(var(--sd-glow) / 0.045) 1px, transparent 1px) 0 0 / 40px 40px,
    linear-gradient(90deg, rgb(var(--sd-glow) / 0.045) 1px, transparent 1px) 0 0 / 40px 40px,
    rgba(2, 5, 12, 0.6);
}
.nge-shotdlg-frame {
  position: relative;
  flex: none;
  box-shadow:
    0 0 0 1px rgb(var(--sd-glow) / 0.4),
    0 10px 36px rgba(0, 0, 0, 0.6),
    0 0 34px rgb(var(--sd-glow) / 0.14);
}
.nge-shotdlg-clip {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #000;
}
.nge-shotdlg-preview,
.nge-shotdlg-markup {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
.nge-shotdlg-markup {
  cursor: crosshair;
  touch-action: none;
}

/* holoscan: one band passes once when the dialog opens, never loops */
.nge-shotdlg-scan {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 9%;
  z-index: 2;
  pointer-events: none;
  opacity: 0;
  background: linear-gradient(180deg, transparent,
    rgb(var(--sd-cyan) / 0.16), transparent);
  animation: nge-shotdlg-scan 1.5s cubic-bezier(.4, 0, .2, 1) 0.35s 1 both;
}
@keyframes nge-shotdlg-scan {
  from { transform: translateY(-100%); opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  to   { transform: translateY(1100%); opacity: 0; }
}

/* holoframe corner brackets, resting in the pushed out position */
.nge-shotdlg-brk {
  position: absolute;
  width: 14px;
  height: 14px;
  pointer-events: none;
  border: 1.5px solid rgb(var(--sd-line) / 0.7);
  transition: transform .42s cubic-bezier(.2, .8, .25, 1);
}
.nge-shotdlg-brk.tl { top: 0; left: 0; border-right: 0; border-bottom: 0; transform: translate(-7px, -7px); }
.nge-shotdlg-brk.tr { top: 0; right: 0; border-left: 0; border-bottom: 0; transform: translate(7px, -7px); }
.nge-shotdlg-brk.bl { bottom: 0; left: 0; border-right: 0; border-top: 0; transform: translate(-7px, 7px); }
.nge-shotdlg-brk.br { bottom: 0; right: 0; border-left: 0; border-top: 0; transform: translate(7px, 7px); }
.nge-shotdlg-frame:hover .nge-shotdlg-brk.tl { transform: translate(-10px, -10px); }
.nge-shotdlg-frame:hover .nge-shotdlg-brk.tr { transform: translate(10px, -10px); }
.nge-shotdlg-frame:hover .nge-shotdlg-brk.bl { transform: translate(-10px, 10px); }
.nge-shotdlg-frame:hover .nge-shotdlg-brk.br { transform: translate(10px, 10px); }

/* ---- right bar ---- */
.nge-shotdlg-side {
  grid-area: side;
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 18px 16px;
  border-left: 1px solid rgb(var(--sd-glow) / 0.18);
  background:
    repeating-linear-gradient(0deg, rgb(var(--sd-cyan) / 0.02) 0 1px, transparent 1px 3px),
    rgba(4, 8, 18, 0.45);
}
.nge-shotdlg-sec {
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.nge-shotdlg-sechead {
  margin: 0;
  padding-bottom: 7px;
  border-bottom: 1px solid rgb(var(--sd-glow) / 0.16);
  color: rgb(var(--sd-line) / 0.85);
}

.nge-shotdlg-dims {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.nge-shotdlg-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1 1 0;
  min-width: 0;
}
.nge-shotdlg-flabel { font-size: 9px; color: rgb(var(--sd-line) / 0.55); }
.nge-shotdlg-field input[type="number"] {
  width: 100%;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgb(var(--sd-glow) / 0.3);
  color: #ffffff;
  border-radius: 6px;
  padding: 7px 8px;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.nge-shotdlg-field input[type="number"]:focus {
  outline: none;
  border-color: rgb(var(--sd-cyan) / 0.75);
  box-shadow: 0 0 0 3px rgb(var(--sd-cyan) / 0.12);
}
.nge-shotdlg-times { color: rgb(var(--sd-line) / 0.45); font-size: 14px; padding-bottom: 8px; }
.nge-shotdlg-px { color: rgb(var(--sd-line) / 0.6); font-size: 12px; padding-bottom: 9px; }

.nge-shotdlg-presets {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.nge-shotdlg-presets button {
  font-family: inherit;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  background: rgb(var(--sd-glow) / 0.07);
  border: 1px solid rgb(var(--sd-glow) / 0.25);
  border-radius: 6px;
  padding: 6px 9px;
  cursor: pointer;
  text-align: left;
  transition: background .15s, border-color .15s, box-shadow .15s;
}
.nge-shotdlg-presets button b {
  font-family: 'Orbitron', 'Inter', sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: rgb(var(--sd-ink));
}
.nge-shotdlg-presets button span {
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 10px;
  color: rgb(var(--sd-line) / 0.5);
  font-variant-numeric: tabular-nums;
}
.nge-shotdlg-presets button:hover {
  background: rgb(var(--sd-glow) / 0.18);
  border-color: rgb(var(--sd-glow) / 0.55);
}
.nge-shotdlg-presets button.is-active {
  background: rgb(var(--sd-glow) / 0.2);
  border-color: rgb(var(--sd-cyan) / 0.75);
  box-shadow: 0 0 12px rgb(var(--sd-cyan) / 0.16), inset 0 0 0 1px rgb(var(--sd-cyan) / 0.2);
}
.nge-shotdlg-presets button.is-active b { color: rgb(var(--sd-cyan)); }

.nge-shotdlg-checks {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.nge-shotdlg-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  color: rgb(var(--sd-ink) / 0.92);
}
.nge-shotdlg-check input {
  -webkit-appearance: none;
  appearance: none;
  flex: none;
  position: relative;
  width: 16px;
  height: 16px;
  margin: 0;
  border: 1px solid rgb(var(--sd-glow) / 0.5);
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: background .15s, border-color .15s, box-shadow .15s;
}
.nge-shotdlg-check input:checked {
  background: rgb(var(--sd-cyan) / 0.22);
  border-color: rgb(var(--sd-cyan) / 0.9);
  box-shadow: 0 0 8px rgb(var(--sd-cyan) / 0.3);
}
.nge-shotdlg-check input:checked::after {
  content: "";
  position: absolute;
  left: 4.5px;
  top: 1.5px;
  width: 4px;
  height: 8px;
  border: solid rgb(var(--sd-cyan));
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.nge-shotdlg-check:hover input { border-color: rgb(var(--sd-cyan) / 0.8); }

.nge-shotdlg button:focus-visible,
.nge-shotdlg-check input:focus-visible,
.nge-shotdlg-pen-size:focus-visible {
  outline: 2px solid rgb(178 216 248);
  outline-offset: 2px;
}

.nge-shotdlg-err {
  background: rgba(220, 60, 60, 0.16);
  border: 1px solid rgba(220, 60, 60, 0.4);
  color: #ffb3b3;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.nge-shotdlg-actions {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 14px;
  border-top: 1px solid rgb(var(--sd-glow) / 0.16);
}
.nge-shotdlg-cancel,
.nge-shotdlg-primary {
  font-family: inherit;
  width: 100%;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background .15s, border-color .15s, box-shadow .15s;
  border: 1px solid transparent;
}
.nge-shotdlg-primary {
  font-family: 'Orbitron', 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  background: rgb(var(--sd-glow) / 0.24);
  border-color: rgb(var(--sd-cyan) / 0.6);
  color: #ffffff;
  box-shadow: 0 0 16px rgb(var(--sd-glow) / 0.2), inset 0 1px 0 rgb(var(--sd-line) / 0.15);
}
.nge-shotdlg-primary:hover:not(:disabled) {
  background: rgb(var(--sd-glow) / 0.36);
  border-color: rgb(var(--sd-cyan) / 0.9);
  box-shadow: 0 0 22px rgb(var(--sd-glow) / 0.32), inset 0 1px 0 rgb(var(--sd-line) / 0.2);
}
.nge-shotdlg-cancel {
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  border-color: rgba(255, 255, 255, 0.16);
  color: rgb(var(--sd-ink) / 0.85);
}
.nge-shotdlg-cancel:hover:not(:disabled) { background: rgba(255, 255, 255, 0.06); }
.nge-shotdlg-primary:disabled,
.nge-shotdlg-cancel:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* keep the pen strip on one line on mid size screens */
@media (max-width: 1300px) {
  .nge-shotdlg-readout-src { display: none; }
}

/* narrow screens: the right bar drops below the image */
@media (max-width: 820px) {
  .nge-shotdlg {
    height: 94vh;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "head"
      "main"
      "side";
  }
  .nge-shotdlg-side {
    max-height: 42vh;
    border-left: 0;
    border-top: 1px solid rgb(var(--sd-glow) / 0.18);
  }
  .nge-shotdlg-toolbar .nge-shotdlg-readout-dim { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .nge-shotdlg { animation: none; }
  .nge-shotdlg-scan { animation: none; display: none; }
  .nge-shotdlg-brk { transition: none; }
}
</style>

<style>
/* While the whole-screen capture runs: modal pop-ups and their blurred
   backdrops step aside, windows stay. */
.nge-shot-sign {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%); z-index: 2147483000;
  display: flex; flex-direction: column; align-items: center; gap: 6px; pointer-events: none;
  padding: 14px 26px 16px; border-radius: 14px;
  font: 600 14px/1.3 'Inter', system-ui, sans-serif; color: #dce6f5;
  background: rgba(8, 14, 28, 0.9); border: 1px solid rgba(100, 200, 255, 0.35);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.55);
}
body.nge-shot-capturing .nge-overlay-blocker,
body.nge-shot-capturing .nge-shotdlg-overlay {
  visibility: hidden !important;
  backdrop-filter: none !important;
}
</style>
